<template>
  <div class="space-y-4">
    <!-- Section Header -->
    <header class="mb-4">
      <div class="flex items-center gap-2">
        <component :is="CalendarDaysIcon" class="h-6 w-6 text-primary" />
        <h3 class="text-lg font-bold text-primary">
          {{ t("pricing.upcoming.afterToday.title") }}
        </h3>
      </div>
      <p class="text-xs mt-1 text-base-content/60">
        {{ t("pricing.upcoming.afterToday.subtitle") }}
      </p>
    </header>

    <!-- Payment pills -->
    <div class="grid grid-cols-1 sm:grid-cols-2 gap-4" aria-live="polite">
      <article
        v-for="item in paymentItems"
        :key="item.aria"
        :class="[
          'flex items-start gap-3 p-5 rounded-2xl border shadow-sm min-h-[160px] transition-colors',
          item.variant === 'current' ? 'bg-purple-50/60 border-purple-300' : 'bg-gray-50 border-gray-300',
        ]"
        role="region"
        :aria-label="item.aria">
        <!-- leading icon -->
        <component :is="item.icon" class="h-6 w-6 mt-1 text-primary flex-shrink-0" />

        <!-- textual content -->
        <div class="flex-1">
          <!-- label for quick scanning -->
          <span
            :class="[
              'block uppercase tracking-wide text-[11px] font-semibold mb-1',
              item.variant === 'current' ? 'text-primary' : 'text-base-content/70',
            ]">
            {{
              item.variant === "current"
                ? t("pricing.upcoming.label.current", "Next 12 months")
                : t("pricing.upcoming.label.renewal", "Renewal")
            }}
          </span>

          <!-- emphasised note -->
          <p
            class="border-l-2 border-primary/40 pl-3 text-base text-base-content font-medium leading-snug md:text-sm"
            v-html="item.note" />

          <!-- amount (can be multi-line) -->
          <p class="text-xl font-bold font-mono mt-3 whitespace-pre-line">
            {{ item.amount }}
          </p>
        </div>
      </article>
    </div>

    <!-- Switch hint for monthly plan -->
    <p
      v-if="billingInterval === 'monthly' && hasYearly"
      class="text-xs mt-3 text-base-content/60 italic"
      v-html="t('pricing.upcoming.switchHint')" />
  </div>
</template>

<script setup lang="ts">
import { computed, defineProps } from "vue";
import { useI18n } from "vue-i18n";
import { ArrowRightIcon as MonthlyIcon, CalendarDaysIcon } from "@heroicons/vue/24/outline";
import { BillingInterval } from "../../types/BillingInterval";

/* ---------- types ---------- */
interface PaymentItem {
  aria: string;
  icon: any;
  amount: string; // may contain line breaks (\n) for multi-line display
  note: string;
  variant: "current" | "renewal";
}

/* ---------- props ---------- */
const props = defineProps<{
  formattedMonthlyRow: string; // e.g. "€ 30,00"
  formattedYearlyRow: string; // e.g. "€ 15,00"
  formattedMonthlyNextDate: string; // ISO 8601 date string (first charge)
  formattedYearlyNextDate: string; // ISO 8601 date string (next annual renewal)
  formattedAnnualRecurringTotal: string;
  daysUntilNext: number;
  hasYearly: boolean; // true when there is at least one yearly-cycle item (domain)
  billingInterval: BillingInterval; // 'monthly' | 'yearly'
}>();

const { t, locale } = useI18n();

/* ---------- helpers ---------- */
function addMonthsPretty(iso: string, months: number): string {
  const d = new Date(iso);
  d.setMonth(d.getMonth() + months);
  // Use the current i18n locale for consistent date formatting
  const browserLocale = locale.value === "nl" ? "nl-NL" : "en-US";
  return d.toLocaleDateString(browserLocale, {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

/* ---------- computed pills ---------- */
const paymentItems = computed<PaymentItem[]>(() => {
  const items: PaymentItem[] = [];

  if (props.billingInterval === "monthly") {
    /* ================= CURRENT TERM (monthly) ================= */
    const firstChargeDate = props.formattedMonthlyNextDate;

    items.push({
      icon: MonthlyIcon,
      variant: "current",
      amount: `${props.formattedMonthlyRow}${t('billing.shortLabels.monthly')}`,
      note: t("pricing.upcoming.pill.monthly.note", {
        date: `<strong>${firstChargeDate}</strong>`,
        endDate: `<span class="font-semibold">${props.formattedYearlyNextDate}</span>`,
      }).replace(/\. /g, ".<br/>"),
      aria: t("pricing.upcoming.pill.monthly.aria"),
    });

    /* ================= RENEWAL ================= */
    //   → always includes the monthly plan; optionally a yearly extra (domain)
    let renewalAmount = `${props.formattedMonthlyRow}${t('billing.shortLabels.monthly')}`;
    let renewalNote = t("pricing.upcoming.pill.renewal.monthly.note");

    if (props.hasYearly) {
      renewalAmount = `${props.formattedMonthlyRow}${t('billing.shortLabels.monthly')}\n${props.formattedYearlyRow}${t('billing.shortLabels.yearly')}`;
      renewalNote += `<br/>${t("pricing.upcoming.pill.renewal.monthly.yearlyDomainRenewal")}`;
    }

    items.push({
      icon: CalendarDaysIcon,
      variant: "renewal",
      amount: renewalAmount,
      note: renewalNote,
      aria: t("pricing.upcoming.pill.renewal.monthly.aria"),
    });
  } else {
    /* ================= CURRENT TERM (yearly prepaid) ================= */
    items.push({
      icon: CalendarDaysIcon,
      variant: "current",
      amount: "€ 0",
      note: t("pricing.upcoming.pill.yearly.zero.note", { date: `<strong>${props.formattedYearlyNextDate}</strong>` }),
      aria: t("pricing.upcoming.pill.yearly.zero.aria"),
    });

    /* ================= RENEWAL ================= */
    const renewalAmount = `${props.formattedAnnualRecurringTotal}${t('billing.shortLabels.year')}`;

    let renewalNote = t("pricing.upcoming.pill.yearly.note", {
      date: `<strong>${props.formattedYearlyNextDate}</strong>`,
    });
    renewalNote += `<br/>${t("pricing.upcoming.pill.yearly.planBilledYearly")}`;

    items.push({
      icon: CalendarDaysIcon,
      variant: "renewal",
      amount: renewalAmount,
      note: renewalNote,
      aria: t("pricing.upcoming.pill.yearly.aria"),
    });
  }

  return items;
});
</script>

<style scoped>
/* Tailwind handles most styling; we bump min‑height earlier and allow whitespace‑pre‑line on amount. */
</style>
